import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StatusBadge, Empty } from "@/components/common";
import { toast } from "sonner";
import { Loader2, Search, Layers, ArrowLeft, Download } from "lucide-react";

const COLS = [
  { key: "group", label: "Group" },
  { key: "engagement_name", label: "Engagement" },
  { key: "phase_name", label: "Phase" },
  { key: "category_name", label: "Category" },
  { key: "name", label: "Request" },
  { key: "first_requested_at", label: "First Requested" },
  { key: "status", label: "Status" },
  { key: "is_due", label: "Is Due?" },
  { key: "days_overdue", label: "Days Overdue" },
  { key: "responsible_engagement", label: "Responsible (Firm)" },
  { key: "responsible_client", label: "Responsible (Client)" },
];

export default function MasterRequests() {
  const nav = useNavigate();
  const { id } = useParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    const url = id ? `/m1/master-requests?engagement_id=${id}` : "/m1/master-requests";
    api.get(url).then((r) => setRows(r.data))
      .catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [id]);

  const engName = rows[0]?.engagement_name;

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => [r.engagement_name, r.phase_name, r.category_name, r.name,
      r.status, r.responsible_engagement, r.responsible_client]
      .some((v) => (v || "").toLowerCase().includes(s)));
  }, [rows, q]);

  const downloadCsv = () => {
    const headers = ["Group", "Engagement", "Phase", "Category", "Request", "First Requested",
      "Age (days)", "Status", "Is Due?", "Days Overdue", "Responsible Engagement User", "Responsible Client User"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [headers.map(esc).join(",")];
    filtered.forEach((r) => lines.push([
      r.group, r.engagement_name, r.phase_name, r.category_name, r.name,
      r.first_requested_at ? new Date(r.first_requested_at).toLocaleDateString() : "",
      r.age_days ?? "", r.status, r.is_due, r.days_overdue,
      r.responsible_engagement, r.responsible_client,
    ].map(esc).join(",")));
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `master-requests${engName ? "-" + engName.replace(/[^a-z0-9]+/gi, "-").toLowerCase() : ""}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${filtered.length} request(s)`);
  };

  return (
    <div>
      {id && (
        <button onClick={() => nav(`/m1/engagements/${id}`)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="back-to-engagement">
          <ArrowLeft className="h-4 w-4" /> Back to engagement
        </button>
      )}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="overline text-azx-collection">Data Collection</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Master Requests</h1>
          <p className="mt-1 text-sm text-slate-500">{id ? `All requests in ${engName || "this engagement"}.` : "Every request across all engagements you can access."}</p>
        </div>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input data-testid="master-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search requests…" className="pl-9" />
          </div>
          <Button data-testid="master-download-btn" variant="outline" onClick={downloadCsv} disabled={filtered.length === 0} className="shrink-0">
            <Download className="mr-2 h-4 w-4" /> Download
          </Button>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        : filtered.length === 0 ? <Empty icon={Layers} title="No requests" hint="Requests from all your engagements will appear here." />
        : (
          <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <table className="w-full text-sm" data-testid="master-table">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800">
                  {COLS.map((c) => (
                    <th key={c.key} className="px-3 py-3 text-left align-bottom font-bold text-slate-700 dark:text-slate-200">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} data-testid={`master-row-${r.id}`} className="border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                    <td className="px-4 py-3 text-slate-400">{r.group}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => nav(`/m1/engagements/${r.engagement_id}`)} className="text-left font-medium text-indigo-600 hover:underline" data-testid={`master-eng-${r.id}`}>{r.engagement_name}</button>
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{r.phase_name}</td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{r.category_name}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => nav(`/m1/engagements/${r.engagement_id}`)} className="text-left font-medium text-indigo-600 hover:underline break-all" data-testid={`master-req-${r.id}`}>{r.name}</button>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap" data-testid={`master-requested-${r.id}`}>
                      {r.first_requested_at ? (
                        <div className="leading-tight">
                          <div className="text-slate-600 dark:text-slate-300">{new Date(r.first_requested_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</div>
                          <div className="text-[11px] text-slate-400">{r.age_days === 0 ? "today" : r.age_days === 1 ? "1 day ago" : `${r.age_days} days ago`}</div>
                        </div>
                      ) : <span className="text-slate-400">-</span>}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{r.is_due}</td>
                    <td className="px-4 py-3 font-medium">
                      {r.days_overdue > 0 ? <span className="text-red-600">{r.days_overdue} Days</span> : <span className="text-slate-400">-</span>}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-slate-600 dark:text-slate-300">{r.responsible_engagement}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-slate-600 dark:text-slate-300">{r.responsible_client}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
